<template>
  <ContentContainer :loadingStatus="isLoading">
    <div>
      <div class="header">{{ $t('agent') }}</div>
      <div class="page-content">
        <div class="agent-list content-wrapper">
          <div>
            <div class="page-header">{{ $t('agentList') }}</div>
            <el-button type="primary" @click="exportAllAgentButtonClick()">Export All Agent</el-button>
          </div>
          <div>
            <el-row>
              <el-col :span="24">
                <el-button type="primary"
                           icon="el-icon-plus"
                           style="float: right"
                           size="small"
                           @click="onPopupAddAgentButtonClick">
                  {{ $t('newAgent') }}
                </el-button>
              </el-col>
            </el-row>
            <el-row>
              <el-col :span="24">
                <div id="search-box">
                  <el-row>
                    <el-col :span="6" style="padding-right: 5px;">
                      <label class="label-form-search">{{$t('keyword')}}</label>
                      <el-input
                        :placeholder="$t('keyword')"
                        v-model="keyword"
                        @input="isSearching = true"
                        size="mini">
                      </el-input>
                    </el-col>
                    <el-col :span="6" style="padding-right: 5px;">
                      <label class="label-form-search">{{$t('type')}}</label>
                      <el-select style="width: 100%"
                                 v-model="agentTypeId"
                                 :placeholder="$t('all')"
                                 size="mini"
                                 @change="isSearching = true"
                                 filterable>
                        <el-option :label="$t('all')" :value="0"></el-option>
                        <el-option
                          v-for="(agentType, index) in sortedAgentTypeList"
                          :key="index"
                          :label="agentType.name"
                          :value="agentType.id">
                        </el-option>
                      </el-select>
                    </el-col>
                    <el-col :span="6">
                      <label class="label-form-search">{{$t('market')}}</label>
                      <el-select style="width: 100%"
                                 v-model="marketId"
                                 :placeholder="$t('all')"
                                 size="mini"
                                 @change="isSearching = true"
                                 filterable>
                        <el-option :label="$t('all')" :value="0"></el-option>
                        <el-option
                          v-for="(market, index) in sortedMarketList"
                          :key="index"
                          :label="market.name"
                          :value="market.id">
                        </el-option>
                      </el-select>
                    </el-col>
                    <el-col :span="6" style="text-align: right;">
                      <br />
                      <br />
                      <el-button type="info"
                                 size="mini"
                                 @click="onResetButtonClick()"
                                 >
                        {{$t('reset')}}
                      </el-button>
                    </el-col>
                  </el-row>
                </div>
              </el-col>
            </el-row>
            <div style="margin-top: 25px">
              <el-table
                agent-data-list
                id="agent-list-table"
                :data="agentListDataSource"
                :row-class-name="getRowClassName"
                header-row-class-name="table-header-color"
                v-loading="isSearching"
                style="width: 100%">
                <el-table-column
                  prop="name"
                  :label="$t('agentName')">
                </el-table-column>
                <el-table-column
                  :label="$t('type')">
                  <template slot-scope="scope">{{ getAgentTypeName(scope.row.agentTypeId) }}</template>
                </el-table-column>
                <el-table-column
                  :label="$t('market')">
                  <template slot-scope="scope">
                    <span class="market-name" v-for="market in groupMarketListByStatus(scope.row.id, STATUS.ENABLE)" :key="market.id">{{ market.name }}</span>
                    <br>
                    <span class="market-name disable" v-for="market in groupMarketListByStatus(scope.row.id, STATUS.DISABLE)" :key="market.id">[{{ $t('STATUS.DISABLE') }}] {{ market.name }}</span>
                  </template>
                </el-table-column>
                <el-table-column
                  prop="internalNote"
                  :label="$t('internalNote')">
                </el-table-column>
                <el-table-column
                  width="120"
                  :label="$t('status')">
                  <template slot-scope="scope">
                    <el-tag
                      :type="scope.row.isEnable === 1 ? 'success' : 'danger'" size="mini"
                      disable-transitions>{{ getAgentStatus(scope.row.isEnable) }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column>
                  <template slot-scope="scope">
                    <el-button type="primary" size="small" icon="el-icon-edit" plain circle @click="onEditButtonClick(scope.row.id)"></el-button>
                  </template>
                </el-table-column>
              </el-table>
              <div style="margin-top: 10px">
                <el-pagination
                  background
                  layout="prev, pager, next"
                  :total="filterAgent().length"
                  :page-size="pageSize"
                  :pager-count="11"
                  :current-page="currentPage"
                  @current-change="changePage">
                </el-pagination>
              </div>

              <PopupAddAgent
                :visible="isPopupAddAgentVisible"
                :agent-type-list="agentTypeList"
                @onClose="isPopupAddAgentVisible = false"
                @onAdded="onAddAgentSuccess">
              </PopupAddAgent>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ContentContainer>
</template>

<script>
import { STATUS, VUEX_EVENT } from '@/configs/constant'
import i18nMessages from '@/translations/Agent/List.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import PopupAddAgent from './components/PopupAddAgent'
import AgentService from '@/services/agent-service'
import AgentApi from '@/api/agent-api'
import LocalStorageFactory from '@/factories/local-storage-factory'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import { filterDataByKeyword, getDataOnPaging, adjustCurrentPage } from '@/services/general-service'
import { delay, getStatusName } from '@/helpers'
import { get, debounce } from 'lodash'

export default {
  name: 'AgentList',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { PopupAddAgent, ContentContainer },
  i18n: { messages: i18nMessages },
  created: async function () {
    try {
      this.isLoading = true

      const result = await Promise.all([
        AgentService.getAgentList(),
        AgentApi.initData()
      ])

      this.agentList = result[0]
      const initData = result[1]
      const marketList = initData.marketList
      const agentTypeList = initData.agentTypeList

      this.marketList = marketList
      this.agentTypeList = agentTypeList
      this._sortData()
      this.sortedAgentTypeList = this._sortingAgentType(agentTypeList)
      this.sortedMarketList = this._sortingMarket(marketList)
      this.marketListById = this._prepareMarketListById(marketList)
      this.getAgentListDataSource()
    } catch (err) {
      this.alertError(err.message)
    }

    this.isLoading = false
  },
  data () {
    return {
      STATUS,
      isLoading: false,
      isPopupAddAgentVisible: false,
      isSearching: false,
      agentList: [],
      marketList: [],
      agentTypeList: [],
      enabledMarketList: [],
      disabledMarketList: [],
      currentPage: 1,
      pageSize: 10,
      sortedAgentTypeList: [],
      sortedMarketList: [],
      marketListById: {},
      agentListDataSource: [],
      keyword: '',
      agentTypeId: 0,
      marketId: 0
    }
  },
  watch: {
    keyword: debounce(function () {
      this.isSearching = false
    }, 300),
    agentTypeId: debounce(function () {
      this.isSearching = false
    }, 300),
    marketId: debounce(function () {
      this.isSearching = false
    }, 300),
    isSearching: function (value) {
      if (!value) {
        this.getAgentListDataSource()
      }
    }
  },
  methods: {
    getAgentStatus: function (status) {
      return this.$t(getStatusName(status))
    },
    changePage: function (page) {
      this.currentPage = page
      this.getAgentListDataSource()
    },
    getAgentListDataSource: function () {
      const filterAgent = this.filterAgent()
      this.currentPage = adjustCurrentPage(filterAgent.length, this.currentPage, this.pageSize)

      const agentList = getDataOnPaging(filterAgent, this.currentPage, this.pageSize)
      this.agentListDataSource = [...agentList]
    },
    filterAgent: function () {
      const searchKeys = ['name']
      let filteredAgentList = filterDataByKeyword(this.agentList, this.keyword, searchKeys)
      filteredAgentList = this._filterAgentType(filteredAgentList)
      filteredAgentList = this._filterMarket(filteredAgentList)

      return filteredAgentList
    },
    _filterAgentType: function (agentList) {
      if (this.agentTypeId === 0) {
        return agentList
      }

      return agentList.filter(agent => agent.agentTypeId === this.agentTypeId)
    },
    _filterMarket: function (agentList) {
      if (this.marketId === 0) {
        return agentList
      }

      const market = this.marketListById[this.marketId]
      const applyAgentIdList = get(market, 'agentIdList', [])

      return agentList.filter(agent => applyAgentIdList.includes(agent.id) === true)
    },
    onPopupAddAgentButtonClick: function () {
      this.isPopupAddAgentVisible = true
    },
    onAddAgentSuccess: function (event) {
      this.agentList.push(event.agent)

      if (event.agentType !== undefined) {
        this.agentTypeList.push(event.agentType)
      }

      this._sortData()
      this.getAgentListDataSource()
      LocalStorageFactory.setAgentList(this.agentList)
    },
    onEditButtonClick: function (agentId) {
      this.$store.dispatch(VUEX_EVENT.GENERAL.SET_ACTIVE_AGENT_ID, agentId)
      this.$router.push({
        name: 'AgentInfo'
      })
    },
    exportAllAgentButtonClick: async function () {
      this.isLoading = true

      const blob = await AgentApi.exportAgent()

      const link = document.createElement('a')
      link.download = 'Agent List'
      link.href = window.URL.createObjectURL(blob)
      link.style.display = 'none'

      document.body.appendChild(link)

      link.click()

      window.URL.revokeObjectURL(link.href)

      this.isLoading = false
    },
    groupMarketListByStatus: function (agentId, status) {
      const marketList = this.marketList.filter((market) => {
        const agentIdList = get(market, 'agentIdList', [])
        const agent = agentIdList.find(id => id === agentId)

        if (agent !== undefined && market.isEnable === status) {
          return market
        }
      })

      return marketList
    },
    getAgentTypeName: function (agentTypeId) {
      const agentType = this.agentTypeList.find(agentType => agentType.id === agentTypeId)
      const agentTypeName = get(agentType, 'name', '-')

      return agentTypeName
    },
    getRowClassName: function ({ row, rowIndex }) {
      if (row.isEnable === 1) {
        return ''
      }
      return 'disable-row'
    },
    onResetButtonClick: async function () {
      this.isSearching = true
      this.keyword = ''
      this.agentTypeId = 0
      this.marketId = 0

      await delay(300)
      this.isSearching = false
    },
    _sortData: function () {
      this.agentList.sort((prev, next) => {
        return (prev.name.toLowerCase() < next.name.toLowerCase()) ? -1 : 1
      })

      this.agentTypeList.sort((prev, next) => {
        return (prev.name.toLowerCase() < next.name.toLowerCase()) ? -1 : 1
      })
    },
    _sortingAgentType: function (agentTypeList) {
      const sortedAgentTypeList = agentTypeList.sort((a, b) => {
        return (a.name.toLowerCase() < b.name.toLowerCase()) ? -1 : 1
      })

      return sortedAgentTypeList
    },
    _sortingMarket: function (marketList) {
      const sortedMarketList = marketList.sort((a, b) => {
        return (a.name.toLowerCase() < b.name.toLowerCase()) ? -1 : 1
      })

      return sortedMarketList
    },
    _prepareMarketListById: function (marketList) {
      const marketListById = {}
      for (let market of marketList) {
        const id = market.id
        marketListById[id] = market
      }

      return marketListById
    }
  }
}
</script>

<style scoped>
  #agent-list-table[agent-data-list] {
    width: 100%;
    border: 1px solid #dddddd;
    border-radius: 5px;
    box-shadow: 0px 3px 5px 1px #eeeeee;
  }
  .el-table th {
    background-color: #eeeeee;
  }
  br:first-child {
    display: none;
  }
  span.market-name:not(:last-of-type)::after {
     content: ", ";
   }
  span.market-name.disable {
    color: #d2d1d1;
  }
  #search-box{
    margin: 20px 0;
    padding: 15px 20px;
    background: #eeeeee;
    border: solid 1px #d8d6d6;
    border-radius: 5px;
  }
  .label-form-search {
    font-size: 13px;
    font-weight: 100;
  }
</style>
